import { defineStore, acceptHMRUpdate } from 'pinia';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';
import moment from '@/utils/moment-wrapper.ts';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import {
    deleteLens,
    deleteLeoMemory,
    fetchConversation,
    fetchLenses,
    fetchLeoMemories,
    fetchUserConversations,
    fetchConversationFilePage as fetchConversationFilePageApi,
    getAskPendoPostRequest,
    getInitialState,
    cleanupIncompleteStoppedMessages,
    parseMessageContent,
    STOPPED_BY_NAVIGATION_MESSAGE,
    STOPPED_BY_USER_MESSAGE,
    postLens,
    postReportTrigger,
    putLens,
    putLeoMemory,
} from '../vuex/modules/ask-pendo/ask-pendo.utils.js';
import { StreamingProcessor } from '../vuex/modules/ask-pendo/ask-pendo.streaming-processor.js';
import { startPollingReport as runReportPoll } from '../vuex/modules/ask-pendo/report-polling.js';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useAuthStore } from '@/stores/auth.store';

const ACTIVE_LENS_SETTING = 'leoActiveLensId';

/**
 * A conversation carries page context only when user_context describes something the user
 * is looking at. Suggested questions alone are just starter prompts, so a conversation
 * opened from the top nav on an uncontextualized page still gets the active lens.
 */
export function hasPageContext (userContext) {
    if (isEmpty(userContext)) return false;

    return Object.keys(userContext).some((key) => key !== 'suggestedQuestions');
}

const pollCancellers = new Map();
function pollKey (conversationId, reportId) {
    return `${conversationId}::${reportId}`;
}

let loadLensesPromise = null;
let restoreLensPromise = null;
let lensMutationEpoch = 0;
let loadMemoriesPromise = null;
let memoryMutationEpoch = 0;

export const useAskPendoStore = defineStore('askPendo', {
    state: getInitialState,
    getters: {
        activeLens: (state) => state.lenses.find((lens) => lens.id === state.activeLensId) || null,
        awaitingFirstResponse: (state) => (id) => {
            const convo = state.conversations[id];
            const hasMessages = convo && convo.messages;
            const isFirstMessage = hasMessages && convo.messages.length === 1;

            return isFirstMessage && convo.messages[0].type === 'user' && convo.messages[0].metadata?.heading_type !== 'ask_question';
        },
        conversationById: (state) => (id) => state.conversations[id] || null,
        conversationHasMessages: (state) => (id) => {
            const convo = state.conversations[id];

            return convo && Array.isArray(convo.messages) && convo.messages.length > 0;
        },
        getArtifactConfig: (state) => (artifactId) => state.artifactConfig[artifactId] || null,
        getArtifactMenuItems: (state) => (artifactId) => state.artifactMenuItems[artifactId] || [],
        getConversationContext: (state) => (route) => {
            const conversationId = route?.name === 'LeoConversation'
                ? route.params.id
                : route?.query?.leo;

            if (!conversationId) return null;

            return state.conversations[conversationId]?.user_context;
        },
        getConversationFile: (state) => (conversationId, filePath) => {
            if (!conversationId || !filePath) return null;

            return state.conversations[conversationId]?.files?.[filePath] ?? null;
        },
        isResponsePending: (state) => (id) => state.pendingResponses[id] || false,
        pendingReportsForConversation: (state) => (id) => {
            const bucket = state.pendingReports?.[id];
            if (!bucket) return [];

            return Object.values(bucket);
        },
        hasPendingReports: (state) => (id) => {
            const bucket = state.pendingReports?.[id];

            return !!bucket && Object.keys(bucket).length > 0;
        },
    },
    actions: {
        addMessage ({ conversationId, type, content, metadata = {}, data = {}, streamId = null }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const message = {
                type,
                content,
                timestamp: moment().toISOString(),
                metadata,
                ...data,
            };

            if (streamId) {
                message.streamId = streamId;
            }

            conversation.messages.push(message);
            cleanupIncompleteStoppedMessages(conversation.messages);
        },
        addStateToConversation ({ conversationId, fieldName, fieldData }) {
            const conversation = this.conversations[conversationId];
            conversation.state_details[fieldName] = fieldData;
        },
        clearActiveStreamId ({ conversationId }) {
            if (this.activeStreamIds) {
                delete this.activeStreamIds[conversationId];
            }
        },
        clearAbortController ({ conversationId }) {
            delete this.abortControllers[conversationId];
        },
        clearArtifactMenuItems (artifactId) {
            delete this.artifactMenuItems[artifactId];
        },
        removeLastExchange ({ conversationId }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const { messages, artifacts } = conversation;

            let lastUserIndex = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i]?.type === 'user') {
                    lastUserIndex = i;
                    break;
                }
            }
            if (lastUserIndex === -1) return;

            const removedArtifactIds = new Set();
            for (let i = lastUserIndex; i < messages.length; i++) {
                const ids = messages[i]?.metadata?.artifact_ids;
                if (Array.isArray(ids)) {
                    for (const id of ids) removedArtifactIds.add(id);
                }
            }

            messages.length = lastUserIndex;

            if (artifacts && removedArtifactIds.size) {
                for (const id of removedArtifactIds) {
                    delete artifacts[id];
                }
            }

            if (conversation.last_artifact_id && removedArtifactIds.has(conversation.last_artifact_id)) {
                delete conversation.last_artifact_id;
            }
        },
        removeIncompleteMessages ({ conversationId, streamId = null, stoppedByNavigation = false }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const { messages } = conversation;
            const isBlank = (item) => !item || item.trim() === '';
            const stopNotice = stoppedByNavigation ? STOPPED_BY_NAVIGATION_MESSAGE : STOPPED_BY_USER_MESSAGE;

            const isIncompleteAssistant = (msg, targetStreamId) =>
                msg.type === 'assistant' &&
                msg.tool_calls?.length > 0 &&
                isBlank(parseMessageContent(msg).content) &&
                (!targetStreamId || msg.streamId === targetStreamId);

            const cleanAssistant = (msg) => {
                const cleaned = { ...msg };
                delete cleaned.tool_calls;
                if (isBlank(parseMessageContent(cleaned).content)) cleaned.content = stopNotice;

                return cleaned;
            };

            const toolCallIds = new Set();

            for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (isIncompleteAssistant(msg, streamId)) {
                    for (const tc of msg.tool_calls) toolCallIds.add(tc.id);
                    messages[i] = cleanAssistant(msg);
                    break;
                }
            }

            if (toolCallIds.size > 0) {
                remove(messages, (msg) => msg && msg.type === 'tool' && toolCallIds.has(msg.tool_call_id));
            }
            cleanupIncompleteStoppedMessages(messages);
        },
        setAbortController ({ conversationId, controller }) {
            this.abortControllers[conversationId] = controller;
        },
        setActiveLens (lensId, { persist = true } = {}) {
            this.activeLensId = lensId;
            if (persist) {
                this.lensRestoreState = 'done';
                useUserSettingsStore()
                    .update({ namespaced: true, name: ACTIVE_LENS_SETTING, value: lensId })
                    .catch(() => {});
            }
        },
        setActiveStreamId ({ conversationId, streamId }) {
            this.activeStreamIds[conversationId] = streamId;
        },
        setArtifactConfig ({ artifactId, config }) {
            this.artifactConfig[artifactId] = assign({}, this.artifactConfig[artifactId], config);
        },
        setArtifactMenuItems ({ artifactId, items }) {
            this.artifactMenuItems[artifactId] = items;
        },
        setConfigDrawerState ({ isOpen }) {
            this.configDrawerOpen = isOpen;
        },
        setMentionShowOutOfLensScope ({ show }) {
            this.mentionShowOutOfLensScope = show;
        },
        setMentionScopeLensName ({ name }) {
            this.mentionScopeLensName = name;
        },
        setConversation ({ id, conversation }) {
            this.conversations[id] = conversation;
        },
        setConversationUserContext ({ id, userContext }) {
            const conversation = this.conversations[id];
            // Once the user has cleared the context, nothing may put one back.
            if (!conversation || conversation.contextDismissed) return;

            conversation.user_context = userContext;
        },
        // A dismissal outlives navigation and a server response. Nothing restores the context.
        dismissConversationUserContext ({ id }) {
            if (this.conversations[id]) {
                this.conversations[id].user_context = null;
                this.conversations[id].contextDismissed = true;
            }
        },
        setConversationLockedLens ({ conversationId, lockedLens }) {
            if (this.conversations[conversationId]) {
                this.conversations[conversationId].lensLocked = true;
                this.conversations[conversationId].lockedLens = lockedLens;
            }
        },
        setUserConversations ({ conversations }) {
            this.userConversations = conversations;
        },
        setRecentConversations ({ conversations }) {
            this.recentConversations = conversations;
        },
        setEmployeeConfig ({ employeeConfig }) {
            this.employeeConfig = employeeConfig;
        },
        setCustomInstructions ({ customInstructions }) {
            this.customInstructions = customInstructions;
        },
        setPreferredAppId ({ preferredAppId }) {
            this.preferredAppId = preferredAppId || 0;
        },
        setResponsePending ({ id, isPending }) {
            this.pendingResponses[id] = isPending;
        },
        setWorkflowState ({ conversationId, workflowState }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            conversation.workflow_state = workflowState;
        },
        setPlanState ({ conversationId, planState }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            conversation.plan = planState;
        },
        updateTodo ({ conversationId, updatedTodo }) {
            const conversation = this.conversations[conversationId];
            if (!conversation) return;

            const todos = conversation.plan.todos.map((t) => (t.id === updatedTodo.id ? updatedTodo : t));
            conversation.plan = { ...conversation.plan, todos };
        },
        addPendingReport ({ conversationId, entry }) {
            if (!conversationId || !entry?.report_id) return;
            if (!this.pendingReports[conversationId]) {
                this.pendingReports[conversationId] = {};
            }
            if (!this.pendingReports[conversationId][entry.report_id]) {
                this.pendingReports[conversationId][entry.report_id] = { ...entry };
            }
        },
        removePendingReport ({ conversationId, reportId }) {
            const bucket = this.pendingReports[conversationId];
            if (!bucket) return;
            delete bucket[reportId];
            if (Object.keys(bucket).length === 0) {
                delete this.pendingReports[conversationId];
            }
        },
        setPendingReports ({ conversationId, entries }) {
            if (!conversationId) return;
            const bucket = {};
            for (const entry of (entries || [])) {
                if (entry?.report_id) bucket[entry.report_id] = { ...entry };
            }
            if (Object.keys(bucket).length === 0) {
                delete this.pendingReports[conversationId];
            } else {
                this.pendingReports[conversationId] = bucket;
            }
        },
        updateArtifact ({ conversationId, artifactId, updates }) {
            const conversation = this.conversations[conversationId];
            if (!conversation?.artifacts?.[artifactId]) return;

            const artifact = conversation.artifacts[artifactId];
            artifact.data = { ...artifact.data, ...updates };
        },
        applyLiveArtifactRefresh ({ conversationId, artifactId, data, live }) {
            const conversation = this.conversations[conversationId];
            const artifact = conversation?.artifacts?.[artifactId];
            if (!artifact) return;

            if (data) artifact.data = data;
            if (live) artifact.live = live;
        },
        async createConversation ({
            id,
            prompt = null,
            type = 'user',
            metadata = {},
            artifacts = {},
            // eslint-disable-next-line camelcase
            user_context = {},
            files = {},
            suggestedPrompt = false,
            // Only a conversation opened to describe wherever the user is may follow them.
            contextFollowsPage = false,
        }) {
            const conversation = {
                conversationId: id,
                messages: [],
                artifacts,
                // eslint-disable-next-line camelcase
                user_context,
                files,
                suggestedPrompt,
                contextFollowsPage,
            };

            this.setConversation({ id, conversation });

            const hasPrompt = typeof prompt === 'string' && prompt.trim().length > 0;
            if (hasPrompt) {
                this.addMessage({
                    conversationId: id,
                    type,
                    content: prompt,
                    metadata,
                });
            }

            return conversation;
        },
        async loadAll ({ reFetch = false } = {}) {
            if (this.userConversations.length > 0 && !reFetch) {
                return this.userConversations;
            }

            const { data } = await fetchUserConversations();
            this.setUserConversations({ conversations: data });

            return data;
        },
        async loadRecent ({ reFetch = false, limit = 3 } = {}) {
            if (this.recentConversations.length > 0 && !reFetch) {
                return this.recentConversations;
            }

            const { data } = await fetchUserConversations({ limit });
            this.setRecentConversations({ conversations: data });

            return data;
        },
        async loadLenses ({ reFetch = false } = {}) {
            if (!hasSegmentFlag('leoLens')) {
                return this.lenses;
            }

            if (loadLensesPromise) {
                return loadLensesPromise.catch((error) => {
                    if (this.lensesLoaded) {
                        return this.lenses;
                    }

                    throw error;
                });
            }

            if (this.lensesLoaded && !reFetch) {
                return this.lenses;
            }

            let promise = null;
            promise = (async () => {
                try {
                    const epochAtFetch = lensMutationEpoch;
                    const { data } = await fetchLenses();
                    if (lensMutationEpoch === epochAtFetch) {
                        this.lensesLoaded = true;
                        this.lenses = data || [];
                    }

                    return this.lenses;
                } finally {
                    if (loadLensesPromise === promise) {
                        loadLensesPromise = null;
                    }
                }
            })();
            loadLensesPromise = promise;

            return promise;
        },
        async restoreActiveLens () {
            if (!hasSegmentFlag('leoLens')) {
                this.lensRestoreState = 'done';

                return;
            }

            if (this.lensRestoreState === 'done') {
                return;
            }

            if (restoreLensPromise) {
                return restoreLensPromise;
            }

            const promise = (async () => {
                try {
                    const userSettings = useUserSettingsStore();
                    await userSettings.loadAll({ reFetch: this.lensRestoreState === 'failed' });
                    if (isEmpty(userSettings.map)) {
                        throw userSettings.error || new Error('user settings unavailable');
                    }
                    await this.loadLenses();
                    if (this.lensRestoreState !== 'done') {
                        this.applyPersistedLens();
                        this.lensRestoreState = 'done';
                    }
                } catch (error) {
                    if (this.lensRestoreState !== 'done') {
                        this.lensRestoreState = 'failed';
                    }
                    throw error;
                } finally {
                    if (restoreLensPromise === promise) {
                        restoreLensPromise = null;
                    }
                }
            })();
            restoreLensPromise = promise;

            return restoreLensPromise;
        },
        applyPersistedLens () {
            const savedLensId = useUserSettingsStore().settingByName(ACTIVE_LENS_SETTING, true)?.value;
            if (savedLensId && this.lenses.some((lens) => String(lens.id) === String(savedLensId))) {
                this.activeLensId = String(savedLensId);
            }
        },
        async addLens (lens) {
            const { data } = await postLens(lens);
            lensMutationEpoch += 1;
            this.lenses.push(data);

            return data;
        },
        async updateLens (lens) {
            const { data } = await putLens(lens);
            lensMutationEpoch += 1;
            this.lenses = this.lenses.map((existingLens) => (existingLens.id === data.id ? data : existingLens));

            return data;
        },
        async removeLens (id) {
            await deleteLens(id);
            lensMutationEpoch += 1;
            this.lenses = this.lenses.filter((lens) => lens.id !== id);
            if (this.activeLensId === id) {
                this.setActiveLens(null);
            }
        },
        async loadMemories ({ reFetch = false } = {}) {
            if (!hasSegmentFlag('leoLongTermMemory') || !hasSegmentFlag('leoDeepAgents')) {
                return this.memories;
            }

            if (loadMemoriesPromise) {
                return loadMemoriesPromise.catch((error) => {
                    if (this.memoriesLoaded) {
                        return this.memories;
                    }

                    throw error;
                });
            }

            if (this.memoriesLoaded && !reFetch) {
                return this.memories;
            }

            let promise = null;
            promise = (async () => {
                try {
                    const epochAtFetch = memoryMutationEpoch;
                    const { data } = await fetchLeoMemories();
                    if (memoryMutationEpoch === epochAtFetch) {
                        this.memoriesLoaded = true;
                        this.memories = data?.memories || [];
                    }

                    return this.memories;
                } finally {
                    if (loadMemoriesPromise === promise) {
                        loadMemoriesPromise = null;
                    }
                }
            })();
            loadMemoriesPromise = promise;

            return promise;
        },
        async updateMemory ({ id, content }) {
            const { data } = await putLeoMemory(id, content);
            memoryMutationEpoch += 1;
            const saved = { id, content, ...data?.memory };
            this.memories = this.memories.map((memory) => (memory.id === id ? { ...memory, ...saved } : memory));

            return saved;
        },
        async removeMemory (id) {
            await deleteLeoMemory(id);
            memoryMutationEpoch += 1;
            this.memories = this.memories.filter((memory) => memory.id !== id);
        },
        async loadOne ({ id, reFetch = false }) {
            if (!reFetch && this.conversationById(id)) {
                return Promise.resolve();
            }

            try {
                const { data } = await fetchConversation(id);
                this.setConversation({
                    id: data.conversationId,
                    conversation: data,
                });

                if (Array.isArray(data.pending_reports) && data.pending_reports.length > 0) {
                    this.hydratePendingReports({
                        conversationId: data.conversationId,
                        entries: data.pending_reports,
                    });
                }

                return data;
            } catch (error) {
                if (error.response?.status === 403 && error.response?.data?.reason === 'app_entitlement') {
                    error.status = 403;
                    error.reason = 'app_entitlement';
                    error.restrictedApps = error.response.data.restrictedApps;
                }
                throw error;
            }
        },
        async sendConversation ({ id, type, revision, force, suggestedPrompt = false, onStreamRead = () => {}, onStreamEnd = () => {} }) {
            this.startLoading(id);
            const conversation = this.conversations[id];

            if (!conversation) {
                this.stopLoading(id);
                throw new Error('Conversation not found');
            }

            const postRequest = getAskPendoPostRequest(type);
            if (!postRequest) {
                this.stopLoading(id);
                throw new Error('Invalid conversation type');
            }

            const existingController = this.abortControllers[conversation.conversationId];
            if (existingController) {
                existingController.abort();
                this.clearAbortController({ conversationId: conversation.conversationId });
            }

            const abortController = new AbortController();
            this.setAbortController({ conversationId: conversation.conversationId, controller: abortController });

            if (type === 'chat' && ['pending', 'failed'].includes(this.lensRestoreState)) {
                await this.restoreActiveLens().catch(() => {});
                if (hasPageContext(conversation.user_context) && this.activeLensId) {
                    this.setActiveLens(null, { persist: false });
                }
            }

            const existingConversation = this.conversationById(conversation.conversationId);
            const lensToSend = existingConversation?.lensLocked ? existingConversation.lockedLens : this.activeLens;

            try {
                const data = await postRequest(
                    conversation,
                    this.employeeConfig,
                    { revision, force, signal: abortController.signal },
                    useAuthStore().user?.jobRole || '',
                    this.customInstructions || '',
                    suggestedPrompt,
                    this.preferredAppId || 0,
                    // Page context owns scoping — a context conversation never sends a lens.
                    hasPageContext(conversation.user_context) ? null : lensToSend,
                );

                if (data instanceof ReadableStream) {
                    let stringBuffer = '';

                    const streamStartTime = Date.now();
                    const streamId = `${conversation.conversationId}_${streamStartTime}`;

                    this.setActiveStreamId({ conversationId: conversation.conversationId, streamId });

                    const streamingProcessor = new StreamingProcessor({
                        onData: (chunk) => {
                            const currentActiveStreamId = this.activeStreamIds[conversation.conversationId];
                            if (currentActiveStreamId !== streamId) {
                                return;
                            }
                            switch (chunk.type) {
                                case 'assistant':
                                    if (chunk.tool_calls?.length > 0) {
                                        this.addMessage({
                                            conversationId: conversation.conversationId,
                                            type: 'assistant',
                                            content: '',
                                            data: chunk,
                                            streamId,
                                        });
                                    }
                                    stringBuffer += chunk.content;
                                    break;
                                case 'tool':
                                    this.addMessage({
                                        conversationId: conversation.conversationId,
                                        type: 'tool',
                                        content: chunk.content,
                                        data: chunk,
                                        streamId,
                                    });
                                    break;
                                case 'workflow_state':
                                    this.setWorkflowState({
                                        conversationId: conversation.conversationId,
                                        workflowState: chunk.workflow,
                                    });
                                    break;
                                case 'plan_state':
                                    this.setPlanState({
                                        conversationId: conversation.conversationId,
                                        planState: chunk.plan,
                                    });
                                    break;
                                case 'todo_update':
                                    this.updateTodo({
                                        conversationId: conversation.conversationId,
                                        updatedTodo: chunk.todo,
                                    });
                                    break;
                                case 'pending_report':
                                    break;
                            }
                            onStreamRead({ ...chunk, streamId });
                        },
                        onEnd: (finalState) => {
                            const activeStreamId = this.activeStreamIds[conversation.conversationId];
                            const isActiveStream = activeStreamId === streamId;

                            if (finalState?.lensLocked && isActiveStream) {
                                this.setConversationLockedLens({
                                    conversationId: conversation.conversationId,
                                    lockedLens: finalState.lockedLens ?? null,
                                });
                            }

                            if (!finalState || !stringBuffer || !isActiveStream) {
                                onStreamEnd();

                                return;
                            }

                            this.$patch((state) => {
                                const previous = state.conversations[conversation.conversationId];
                                state.conversations[conversation.conversationId] = {
                                    conversationId: conversation.conversationId,
                                    lensLocked: previous?.lensLocked,
                                    lockedLens: previous?.lockedLens,
                                    contextFollowsPage: previous?.contextFollowsPage,
                                    ...finalState,
                                    ...(previous?.contextDismissed && { user_context: null, contextDismissed: true }),
                                };
                                delete state.activeStreamIds[conversation.conversationId];
                            });

                            if (Array.isArray(finalState.pending_reports)) {
                                this.reconcilePendingReports({
                                    conversationId: conversation.conversationId,
                                    entries: finalState.pending_reports,
                                });
                            }

                            onStreamEnd();
                        },
                        onError: (error) => {
                            throw error;
                        },
                    });

                    await streamingProcessor.process(data);

                    return;
                }

                if (data.body.error) {
                    this.stopLoading(id);
                    throw new Error(data.body.error);
                }

                const previous = this.conversations[id];
                const conversationResponse = {
                    id,
                    lensLocked: previous?.lensLocked,
                    lockedLens: previous?.lockedLens,
                    contextFollowsPage: previous?.contextFollowsPage,
                    ...data.body,
                    ...(previous?.contextDismissed && { user_context: null, contextDismissed: true }),
                    messages: data.body.messages.map((msg) => ({
                        ...msg,
                        timestamp: moment().toISOString(),
                    })),
                };
                this.setConversation({ id, conversation: conversationResponse });
            } catch (error) {
                if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message.includes('aborted')) {
                    return;
                }
                const streamError = new Error(error.message);
                streamError.type = error.type;
                streamError.code = error.code;
                streamError.details = error.details;

                throw streamError;
            } finally {
                if (this.abortControllers[conversation.conversationId] === abortController) {
                    this.stopLoading(id);
                    this.clearAbortController({ conversationId: conversation.conversationId });
                }
            }
        },
        async fetchConversationFilePage ({ conversationId, fileId, pageToken, pageSize } = {}) {
            return fetchConversationFilePageApi(conversationId, fileId, { pageToken, pageSize });
        },
        setEmployeeConfigProperty ({ key, value }) {
            const employeeConfig = {
                ...this.employeeConfig,
                [key]: value,
            };
            this.setEmployeeConfig({ employeeConfig });
        },
        unsetEmployeeConfigProperty (key) {
            const { [key]: _removed, ...employeeConfig } = this.employeeConfig;
            this.setEmployeeConfig({ employeeConfig });
        },
        updateCustomInstructions (customInstructions) {
            this.setCustomInstructions({ customInstructions: customInstructions || '' });
        },
        updatePreferredAppId (preferredAppId) {
            this.setPreferredAppId({ preferredAppId: preferredAppId || 0 });
        },
        startLoading (conversationId) {
            this.setResponsePending({ id: conversationId, isPending: true });
        },
        stopLoading (conversationId) {
            this.setResponsePending({ id: conversationId, isPending: false });
        },
        stopResponse ({ conversationId, stoppedByNavigation = false }) {
            const activeStreamId = this.activeStreamIds[conversationId];
            const controller = this.abortControllers[conversationId];
            if (controller) {
                controller.abort();
                this.clearAbortController({ conversationId });
            }
            this.clearActiveStreamId({ conversationId });
            if (activeStreamId) {
                this.removeIncompleteMessages({ conversationId, streamId: activeStreamId, stoppedByNavigation });
            }
            this.stopLoading(conversationId);
        },
        updateConfigDrawerState ({ isOpen }) {
            this.setConfigDrawerState({ isOpen });
        },
        trackPendingReport ({ conversationId, entry }) {
            if (!conversationId || !entry?.report_id) return;
            const existing = this.pendingReports[conversationId]?.[entry.report_id];
            this.addPendingReport({ conversationId, entry });
            if (!existing) {
                this.startPollingReport({ conversationId, reportId: entry.report_id });
            }
        },
        reconcilePendingReports ({ conversationId, entries }) {
            if (!conversationId) return;
            const wanted = new Set();
            for (const entry of (entries || [])) {
                if (entry?.report_id) {
                    wanted.add(entry.report_id);
                    const existing = this.pendingReports[conversationId]?.[entry.report_id];
                    this.addPendingReport({ conversationId, entry });
                    if (!existing) {
                        this.startPollingReport({ conversationId, reportId: entry.report_id });
                    }
                }
            }

            const current = this.pendingReports[conversationId] || {};
            for (const reportId of Object.keys(current)) {
                if (!wanted.has(reportId)) {
                    this.stopPollingReport({ conversationId, reportId });
                    this.removePendingReport({ conversationId, reportId });
                }
            }
        },
        hydratePendingReports ({ conversationId, entries }) {
            if (!conversationId || !Array.isArray(entries)) return;
            for (const entry of entries) {
                if (entry?.report_id) {
                    this.trackPendingReport({ conversationId, entry });
                }
            }
        },
        startPollingReport ({ conversationId, reportId }) {
            if (!conversationId || !reportId) return;
            const key = pollKey(conversationId, reportId);
            const previous = pollCancellers.get(key);
            if (previous) {
                previous();
                pollCancellers.delete(key);
            }

            const cancel = runReportPoll({
                reportId,
                onDone: ({ status, missing }) => {
                    pollCancellers.delete(key);
                    if (!this.pendingReports[conversationId]?.[reportId]) return;
                    const triggerContent = status === 'success' ? 'report_done' : 'report_failed';
                    this.triggerReportSynthesis({
                        conversationId,
                        reportId,
                        triggerContent,
                        missing: missing === true,
                    });
                },
                onError: () => {},
            });

            pollCancellers.set(key, cancel);
        },
        stopPollingReport ({ conversationId, reportId }) {
            if (!conversationId || !reportId) return;
            const key = pollKey(conversationId, reportId);
            const cancel = pollCancellers.get(key);
            if (cancel) {
                cancel();
                pollCancellers.delete(key);
            }
        },
        async triggerReportSynthesis ({ conversationId, reportId, triggerContent }) {
            if (!conversationId || !reportId || !triggerContent) return;

            if (this.abortControllers[conversationId]) {
                this.startPollingReport({ conversationId, reportId });

                return;
            }

            const previousEntry = this.pendingReports[conversationId]?.[reportId];
            this.removePendingReport({ conversationId, reportId });

            this.startLoading(conversationId);

            const abortController = new AbortController();
            this.setAbortController({ conversationId, controller: abortController });

            try {
                const data = await postReportTrigger(
                    conversationId,
                    reportId,
                    triggerContent,
                    { signal: abortController.signal },
                    this.employeeConfig,
                    useAuthStore().user?.jobRole || '',
                    this.customInstructions || '',
                );

                if (!(data instanceof ReadableStream)) {
                    if (previousEntry) {
                        this.addPendingReport({ conversationId, entry: previousEntry });
                        this.startPollingReport({ conversationId, reportId });
                    }

                    return;
                }

                const streamStartTime = Date.now();
                const streamId = `${conversationId}_trigger_${reportId}_${streamStartTime}`;
                this.setActiveStreamId({ conversationId, streamId });

                const streamingProcessor = new StreamingProcessor({
                    onData: (chunk) => {
                        const currentActiveStreamId = this.activeStreamIds[conversationId];
                        if (currentActiveStreamId !== streamId) return;

                        switch (chunk.type) {
                            case 'assistant':
                                if (chunk.tool_calls?.length > 0) {
                                    this.addMessage({
                                        conversationId,
                                        type: 'assistant',
                                        content: '',
                                        data: chunk,
                                        streamId,
                                    });
                                }
                                break;
                            case 'tool':
                                this.addMessage({
                                    conversationId,
                                    type: 'tool',
                                    content: chunk.content,
                                    data: chunk,
                                    streamId,
                                });
                                break;
                            case 'pending_report':
                                break;
                        }
                    },
                    onEnd: (finalState) => {
                        const activeStreamId = this.activeStreamIds[conversationId];
                        const isActiveStream = activeStreamId === streamId;
                        if (!finalState || !isActiveStream) return;

                        this.$patch((state) => {
                            const previous = state.conversations[conversationId];
                            state.conversations[conversationId] = {
                                conversationId,
                                lensLocked: previous?.lensLocked,
                                lockedLens: previous?.lockedLens,
                                contextFollowsPage: previous?.contextFollowsPage,
                                ...finalState,
                                ...(previous?.contextDismissed && { user_context: null, contextDismissed: true }),
                            };
                            delete state.activeStreamIds[conversationId];
                        });

                        if (Array.isArray(finalState.pending_reports)) {
                            this.reconcilePendingReports({
                                conversationId,
                                entries: finalState.pending_reports,
                            });
                        }
                    },
                    onError: (error) => {
                        throw error;
                    },
                });

                await streamingProcessor.process(data);
            } catch (error) {
                if (previousEntry && error?.name !== 'AbortError') {
                    this.addPendingReport({ conversationId, entry: previousEntry });
                    this.startPollingReport({ conversationId, reportId });
                }
            } finally {
                if (this.abortControllers[conversationId] === abortController) {
                    this.stopLoading(conversationId);
                    this.clearAbortController({ conversationId });
                }
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useAskPendoStore, module.hot));
}
